import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigation } from '@/context/NavigationContext'
import { useSettingsStore } from '@/store/settings.store'

export default function Header() {
  const { t, i18n } = useTranslation()
  const { navigate } = useNavigation()
  const [showCalendarMenu, setShowCalendarMenu] = useState(false)
  const [showBackupMenu, setShowBackupMenu] = useState(false)
  const calendarRef = useRef<HTMLDivElement>(null)
  const backupRef = useRef<HTMLDivElement>(null)
  
  const theme = useSettingsStore((state) => state.theme)
  const language = useSettingsStore((state) => state.language)
  const calendar = useSettingsStore((state) => state.calendar)
  const toggleTheme = useSettingsStore((state) => state.toggleTheme)
  const setCalendar = useSettingsStore((state) => state.setCalendar)

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setShowCalendarMenu(false)
      }
      if (backupRef.current && !backupRef.current.contains(event.target as Node)) {
        setShowBackupMenu(false)
      }
    }

    if (showCalendarMenu || showBackupMenu) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showCalendarMenu, showBackupMenu])

  const handleLanguageChange = (lang: 'fa' | 'en') => {
    i18n.changeLanguage(lang)
    localStorage.setItem('language', lang)
    document.documentElement.lang = lang
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr'
  }

  const handleExport = () => {
    // Simple export of all data as JSON
    const allData = {
      settings: useSettingsStore.getState(),
    }
    const dataStr = JSON.stringify(allData, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `moji-planner-backup-${Date.now()}.json`
    link.click()
    setShowBackupMenu(false)
  }

  const handleImport = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.json'
    input.onchange = async (e: any) => {
      try {
        const file = e.target.files[0]
        const text = await file.text()
        const data = JSON.parse(text)
        if (data.settings) {
          useSettingsStore.setState(data.settings)
          alert(t('common.success', 'موفق') + ' - ' + 'Data imported successfully!')
        }
      } catch (err) {
        console.error('❌ Import failed:', err)
        alert('خطا در بارگذاری فایل')
      }
    }
    input.click()
    setShowBackupMenu(false)
  }

  return (
    <header className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 shadow-lg px-6 flex justify-between items-center border-b border-gray-200 dark:border-slate-700" style={{ padding: '12px 24px' }}>
      <button
        onClick={() => navigate('/')}
        className="text-xl font-bold text-gray-900 dark:text-white hover:opacity-80 transition-all duration-300 flex items-center gap-2 group"
      >
        <span className="text-3xl group-hover:scale-110 transition-transform duration-300">🅼️</span>
        <div className="text-left">
          <div className="text-lg font-bold leading-tight">{t('header.brand')}</div>
          <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">{t('header.tagline')}</div>
        </div>
      </button>
      
      <div className="flex gap-1 items-center">
        {/* Calendar Menu */}
        <div className="relative" ref={calendarRef}>
          <button
            onClick={() => setShowCalendarMenu(!showCalendarMenu)}
            className={`p-2.5 rounded-lg transition-all duration-200 text-xl ${
              showCalendarMenu
                ? 'bg-blue-500 text-white shadow-lg'
                : 'hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300'
            }`}
            title="تقویم"
          >
            📆
          </button>
          {showCalendarMenu && (
            <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-slate-700 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-slate-600 overflow-hidden">
              <button
                onClick={() => {
                  setCalendar('jalali')
                  setShowCalendarMenu(false)
                }}
                className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-all duration-150 ${
                  calendar === 'jalali'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-600'
                }`}
              >
                🗓️ جلالی
              </button>
              <button
                onClick={() => {
                  setCalendar('gregorian')
                  setShowCalendarMenu(false)
                }}
                className={`w-full text-left px-4 py-2.5 text-sm font-semibold transition-all duration-150 ${
                  calendar === 'gregorian'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-600'
                }`}
              >
                📅 میلادی
              </button>
            </div>
          )}
        </div>

        {/* Backup Menu */}
        <div className="relative" ref={backupRef}>
          <button
            onClick={() => setShowBackupMenu(!showBackupMenu)}
            className={`p-2.5 rounded-lg transition-all duration-200 text-xl ${
              showBackupMenu
                ? 'bg-green-500 text-white shadow-lg'
                : 'hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-gray-300'
            }`}
            title="پشتیبان‌گیری"
          >
            💾
          </button>
          {showBackupMenu && (
            <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-slate-700 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-slate-600 overflow-hidden">
              <button
                onClick={handleExport}
                className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-white hover:bg-green-100 dark:hover:bg-green-900/20 transition-all duration-150"
              >
                ⬇️ دانلود پشتیبان
              </button>
              <button
                onClick={handleImport}
                className="w-full text-left px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-white hover:bg-green-100 dark:hover:bg-green-900/20 transition-all duration-150"
              >
                ⬆️ بارگذاری پشتیبان
              </button>
            </div>
          )}
        </div>

        {/* Theme Toggle */}
        <button
          onClick={() => {
            console.log('👆 Theme toggle clicked, current theme:', theme)
            toggleTheme()
          }}
          className="p-2.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200 text-xl text-gray-700 dark:text-gray-300"
          title="تغییر پوسته"
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        
        {/* Settings */}
        <button
          onClick={() => navigate('/settings')}
          className="p-2.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-700 transition-all duration-200 text-xl text-gray-700 dark:text-gray-300"
          title="تنظیمات"
        >
          ⚙️
        </button>
      </div>
    </header>
  )
}
